import React, { useCallback, useImperativeHandle, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import { Grid } from '@material-ui/core'
import SydModal from '../../../../commonDesign/SydModal'
import SydButton from '../../../../commonDesign/Button'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'
import SydLabel, { hookFormErrorAdapter } from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import { actionTemplates } from '../../../../../api/coreData'

const NewVersionDialog = React.forwardRef(function NewVersionDialog ({ template, onComplete }, ref) {
  const [dialogState, setDialogState] = useState({ open: false, definition: null })
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const { mutateAsync: addDefinition } = actionTemplates.useAddActionTemplateDefinition()

  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      definitionVersionId: '',
      description: ''
    }
  })

  const close = useCallback(() => {
    setDialogState({ open: false, definition: null })
    setError(null)
    form.reset()
  }, [form])

  useImperativeHandle(ref, () => ({
    open: (initialDefinition) => {
      form.reset({ definitionVersionId: '', description: '' })
      setDialogState({ open: true, definition: initialDefinition || null })
    }
  }), [form])

  const handleSave = useCallback(async (formData) => {
    try {
      setProcessing(true)
      setError(null)

      const definition = dialogState.definition || template?.definition || {}

      const command = {
        actionTemplateId: template.actionTemplateId,
        definition: {
          ...(formData.definitionVersionId ? { definitionVersionId: formData.definitionVersionId } : {}),
          description: formData.description || null,
          dataPipelineCode: definition.dataPipelineCode || null,
          fieldMapping: definition.fieldMapping || {},
          argMapping: definition.argMapping || {},
          actionPipelineCode: definition.actionPipelineCode || '',
          invalidations: definition.invalidations || [],
          authScheme: definition.authScheme || null
        },
        options: {
          setAsActive: true
        }
      }

      await addDefinition(command)
      close()
      onComplete()
    } catch (err) {
      setError(err.message || 'Failed to save definition version')
    } finally {
      setProcessing(false)
    }
  }, [addDefinition, template, dialogState, close, onComplete])

  return (
    <SydModal
      title='Save New Version'
      open={dialogState.open}
      onClose={close}
      size='medium'
      position='top'
    >
      <div style={{ padding: '16px' }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name='definitionVersionId'
              control={form.control}
              rules={{
                maxLength: { value: 32, message: 'Max 32 characters.' }
              }}
              render={(f) => (
                <SydLabel label='Version ID' description='Leave blank to auto-generate' error={hookFormErrorAdapter(f, f.fieldState)}>
                  <SydInput {...f.field} placeholder='e.g. v1, v2, or leave blank' />
                </SydLabel>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name='description'
              control={form.control}
              render={(f) => (
                <SydLabel label='Version Description' description='Optional changelog entry for this version'>
                  <SydInput {...f.field} multiline rows={3} placeholder='Describe what changed...' />
                </SydLabel>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <SydModalActions>
              {error ? <div className='__error'>{error}</div> : null}
              <SydButton variant='ghost' onClick={close} disabled={processing}>Cancel</SydButton>
              <SydButton
                variant='primary'
                onClick={form.handleSubmit(handleSave)}
                processing={processing}
              >
                Save &amp; Set Active
              </SydButton>
            </SydModalActions>
          </Grid>
        </Grid>
      </div>
    </SydModal>
  )
})

NewVersionDialog.propTypes = {
  template: PropTypes.object,
  onComplete: PropTypes.func.isRequired
}

export default NewVersionDialog
